import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { sitesApi, Site } from '../lib/sites';
import { siteControllersApi } from '../lib/site-controllers';
import { areasApi } from '../lib/areas';
import { useAuth } from '../hooks/useAuth';
import SitesList from '../components/sites/SitesList';
import SitesControl from '../components/sites/SitesControl';
import { Plus, List, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { canManageSites } from '../utils/role-utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import LocationControl from '@/components/sites/LocationControl';

export default function SitesPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'list' | 'map' | 'control'>('list');
  const [uiAlert, setUiAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({ open: false, title: 'Notice', message: '' });

  const canCreate = canManageSites(currentUser?.role);
  
  // Get page title based on role
  const getPageTitle = () => {
    switch (currentUser?.role) {
      case 'super_user':
        return 'Sites';
      case 'site_admin':
        return 'My Sites';
      case 'end_user':
        return 'My Sites';
      default:
        return 'Sites';
    }
  };

  const { data: sites, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.getAll(),
  });

  const { data: allControllers, isLoading: controllersLoading } = useQuery({
    queryKey: ['all-controllers', sites?.map((s) => s.id)],
    queryFn: async () => {
      if (!sites || sites.length === 0) return [];
      const controllerPromises = sites.map((site) =>
        siteControllersApi.getBySite(site.id).then((controllers) => ({
          siteId: site.id,
          siteName: site.name,
          controllers,
        }))
      );
      return await Promise.all(controllerPromises);
    },
    enabled: !!sites && sites.length > 0,
  });

  const setCurrentPresetMutation = useMutation({
    mutationFn: ({ areaId, presetId }: { areaId: string; presetId: string }) =>
      areasApi.setCurrentPreset(areaId, presetId),
    onSuccess: (updatedArea) => {
      queryClient.invalidateQueries({ queryKey: ['areas', updatedArea.siteControllerId] });
      queryClient.invalidateQueries({ queryKey: ['area', updatedArea.id] });
      queryClient.invalidateQueries({ queryKey: ['presets', updatedArea.id] });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = err.response?.data?.error || err.message || 'Failed to set current preset';
      setUiAlert({ open: true, title: 'Error', message: errorMessage });
    },
  });

  const handleSetCurrentPreset = (areaId: string, presetId: string | null) => {
    if (presetId === null) return;
    setCurrentPresetMutation.mutate({ areaId, presetId });
  };

  const createMutation = useMutation({
    mutationFn: (data: Partial<Site>) => sitesApi.create(data),
    onSuccess: (newSite) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      // Navigate to the new site's detail page where user can edit in Overview tab
      navigate(`/sites/${newSite.id}`);
    },
  });

  const handleCreate = () => {
    // Create a minimal site and navigate to its detail page
    createMutation.mutate({
      name: 'New Site',
      description: '',
    });
  };

  if (isLoading || controllersLoading) {
    return <div className="p-6">Loading...</div>;
  }

  const controllersBySite =
    allControllers?.reduce<Record<string, typeof allControllers[number]['controllers']>>((acc, entry) => {
      acc[entry.siteId] = entry.controllers;
      return acc;
    }, {}) || {};

  return (
    <>
    <div className="px-2 py-3 sm:px-0">
      <div className="flex flex-row items-center justify-between gap-4 mb-6 flex-wrap">
        <div className="flex-shrink-0">
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">{getPageTitle()}</h1>
          {currentUser?.role === 'end_user' && (
            <p className="text-sm text-muted-foreground mt-1">Sites you have access to via user groups</p>
          )}
          {currentUser?.role === 'site_admin' && (
            <p className="text-sm text-muted-foreground mt-1">Sites assigned to you for management</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 flex-shrink-0">
          {canCreate && (
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Site</span>
            </Button>
          )}
          <Tabs id="sites-tabs" value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'map' | 'control')}>
            <TabsList>
              <TabsTrigger value="list">
                <List className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">List</span>
              </TabsTrigger>
              <TabsTrigger value="map">
                <Map className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Map</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {viewMode === 'list' && (
        <SitesList
          sites={sites || []}
          controllersBySite={controllersBySite}
        />
      )}

      {viewMode === 'map' && (
        <LocationControl mapHeightClassName="min-h-[22rem] h-[28rem]" />
      )}

      {viewMode === 'control' && (
        <SitesControl
          allControllers={allControllers || []}
          onSetCurrentPreset={handleSetCurrentPreset}
        />
      )}
    </div>
    <AlertDialog
      open={uiAlert.open}
      onOpenChange={(open) => setUiAlert((prev) => ({ ...prev, open }))}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{uiAlert.title}</AlertDialogTitle>
          <AlertDialogDescription>{uiAlert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

